import classNames from "classnames";
import {
  MouseEventHandler,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
} from "react";

export type IconButtonVariant = "default" | "primary" | "danger" | "ghost";

export type IconButtonSize = "sm" | "md" | "lg";

type LegacyIconButtonSize = "small" | "medium" | "large";

export type IconButtonTipPlacement = "top" | "bottom" | "left" | "right";

export type IconButtonDotTone = "danger" | "primary" | "secondary";

interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The glyph. Pass a lucide-react element (e.g. `<Trash2 />`); the button
   * injects the size-appropriate `size` and `strokeWidth` unless the element
   * sets its own.
   */
  icon?: ReactNode;
  /**
   * Tooltip text, also used as the `aria-label`. An icon button has no
   * visible text, so every new call site must provide it. Optional only so
   * legacy call sites keep compiling.
   */
  label?: string;
  /** Visual treatment. `ghost` is borderless, for dense toolbars and top strips. */
  variant?: IconButtonVariant;
  /**
   * `sm` 30px · `md` 36px · `lg` 44px. The legacy names
   * `small`/`medium`/`large` map to `sm`/`md`/`lg` and are deprecated.
   */
  size?: IconButtonSize | LegacyIconButtonSize;
  /** Tooltip placement relative to the button. */
  tip?: IconButtonTipPlacement;
  /** Suppress the tooltip; `label` still labels the button for screen readers. */
  noTip?: boolean;
  /** Toggle / selected state (`aria-pressed`), styled like the active nav item. */
  active?: boolean;
  /** Swap the glyph for a spinner and disable interaction (`aria-busy`). */
  loading?: boolean;
  /** Count bubble on the top-right corner; values above 99 render as "99+". */
  badge?: number | null;
  /** Small status dot on the top-right corner; hidden while a badge shows. */
  dot?: boolean;
  /** Dot color. */
  dotTone?: IconButtonDotTone;
  /** Stroke width injected into a lucide glyph. */
  strokeWidth?: number;
  /** @deprecated Pass the element via `icon` instead. */
  customIcon?: ReactNode;
  /** @deprecated Legacy class override; suppresses the variant palette. */
  color?: string;
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
}

const SIZE_ALIASES: Record<LegacyIconButtonSize, IconButtonSize> = {
  small: "sm",
  medium: "md",
  large: "lg",
};

const BUTTON_SIZE_CLASSES: Record<IconButtonSize, string> = {
  sm: "w-[30px] h-[30px]",
  md: "w-9 h-9",
  lg: "w-11 h-11",
};

const GLYPH_SIZES: Record<IconButtonSize, number> = {
  sm: 15,
  md: 17,
  lg: 20,
};

const SPINNER_SIZE_CLASSES: Record<IconButtonSize, string> = {
  sm: "w-[13px] h-[13px]",
  md: "w-[15px] h-[15px]",
  lg: "w-[18px] h-[18px]",
};

const VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  default: classNames(
    "bg-white border-gray-200 text-gray-500",
    "hover:bg-gray-100 hover:border-gray-300 hover:text-gray-700",
    "active:bg-gray-100 active:border-gray-300 active:text-gray-800",
  ),
  primary: classNames(
    "bg-white border-gray-200 text-primary-500",
    "hover:bg-primary-50 hover:border-primary-200 hover:text-primary-600",
    "active:bg-primary-100 active:border-primary-300",
  ),
  danger: classNames(
    "bg-white border-red-200 text-red-500",
    "hover:bg-red-50 hover:border-red-300",
    "active:bg-red-200",
  ),
  ghost: classNames(
    "bg-transparent border-transparent text-gray-500",
    "hover:bg-gray-100 hover:text-gray-700",
    "active:bg-gray-200",
  ),
};

const ACTIVE_CLASSES = classNames(
  "bg-primary-50 border-primary-200 text-primary-700",
  "hover:bg-primary-100 hover:border-primary-300",
);

const DOT_TONE_CLASSES: Record<IconButtonDotTone, string> = {
  danger: "bg-red-500",
  primary: "bg-primary-300",
  secondary: "bg-yellow-500",
};

const TIP_PLACEMENT_CLASSES: Record<IconButtonTipPlacement, string> = {
  top: "bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 origin-bottom",
  bottom: "top-[calc(100%+8px)] left-1/2 -translate-x-1/2 origin-top",
  left: "right-[calc(100%+8px)] top-1/2 -translate-y-1/2 origin-right",
  right: "left-[calc(100%+8px)] top-1/2 -translate-y-1/2 origin-left",
};

const TIP_ARROW_CLASSES: Record<IconButtonTipPlacement, string> = {
  top: "top-full left-1/2 -ml-[3.5px] -mt-[3.5px]",
  bottom: "bottom-full left-1/2 -ml-[3.5px] -mb-[3.5px]",
  left: "left-full top-1/2 -mt-[3.5px] -ml-[3.5px]",
  right: "right-full top-1/2 -mt-[3.5px] -mr-[3.5px]",
};

function sizeGlyph(
  glyph: ReactNode,
  glyphSize: number,
  strokeWidth: number,
): ReactNode {
  if (!isValidElement(glyph) || typeof glyph.type === "string") {
    return glyph;
  }
  const element = glyph as ReactElement<{
    size?: number;
    strokeWidth?: number;
  }>;
  return cloneElement(element, {
    size: element.props.size ?? glyphSize,
    strokeWidth: element.props.strokeWidth ?? strokeWidth,
  });
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      label,
      variant = "default",
      size = "md",
      tip = "top",
      noTip = false,
      active = false,
      loading = false,
      disabled = false,
      badge = null,
      dot = false,
      dotTone = "danger",
      strokeWidth = 2,
      customIcon,
      color,
      onClick,
      type = "button",
      className,
      ...props
    },
    ref,
  ) {
    const resolvedSize = SIZE_ALIASES[size as LegacyIconButtonSize] ?? size;
    const glyphSize = GLYPH_SIZES[resolvedSize];

    let glyph: ReactNode;
    if (loading) {
      glyph = (
        <span
          aria-hidden="true"
          className={classNames(
            "rounded-full border-2 border-current border-t-transparent",
            "opacity-85 motion-safe:animate-spin",
            SPINNER_SIZE_CLASSES[resolvedSize],
          )}
        />
      );
    } else {
      glyph = sizeGlyph(icon ?? customIcon, glyphSize, strokeWidth);
    }

    const badgeValue = typeof badge === "number" && badge !== 0 ? badge : null;
    const showBadge = badgeValue !== null;
    const isDisabled = disabled || loading;
    const showTip = !noTip && !!label;

    return (
      <span className="relative inline-flex group">
        <button
          ref={ref}
          type={type}
          onClick={onClick}
          aria-label={label}
          aria-pressed={active || undefined}
          aria-busy={loading || undefined}
          disabled={isDisabled}
          {...props}
          className={classNames(
            "relative shrink-0 grid place-items-center rounded border",
            "transition-colors duration-150 active:translate-y-px",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-primary-300 focus-visible:border-primary-300",
            "disabled:pointer-events-none disabled:opacity-45",
            loading && "cursor-progress",
            BUTTON_SIZE_CLASSES[resolvedSize],
            color
              ? classNames("hover:bg-primary-50", color)
              : active
                ? ACTIVE_CLASSES
                : VARIANT_CLASSES[variant],
            className,
          )}
        >
          {glyph}
          {showBadge && (
            <span
              className={classNames(
                "absolute -top-[5px] -right-[5px] z-[2]",
                "min-w-[16px] h-[16px] px-[4px]",
                "grid place-items-center rounded-full border-[1.5px] border-white",
                "bg-red-500 text-white text-[10px] font-bold leading-none",
                "tabular-nums",
              )}
            >
              {/* Comfortaa's digit ink sits ~0.4px above the line-box centre at 10px */}
              <span className="translate-y-[0.5px]">
                {badgeValue !== null && badgeValue > 99 ? "99+" : badgeValue}
              </span>
            </span>
          )}
          {!showBadge && dot && (
            <span
              aria-hidden="true"
              className={classNames(
                "absolute -top-px -right-px z-[2] w-2 h-2 rounded-full",
                "border-[1.5px] border-white",
                DOT_TONE_CLASSES[dotTone],
              )}
            />
          )}
        </button>
        {showTip && (
          <span
            role="tooltip"
            className={classNames(
              "absolute z-[60] pointer-events-none whitespace-nowrap",
              "bg-gray-800 text-white text-xs font-medium leading-none",
              "px-2 py-1.5 rounded-md shadow-lg",
              "opacity-0 scale-95 transition-all duration-150",
              "group-hover:opacity-100 group-hover:scale-100",
              "group-hover:delay-300",
              "group-focus-within:opacity-100 group-focus-within:scale-100",
              "group-focus-within:delay-0",
              TIP_PLACEMENT_CLASSES[tip],
            )}
          >
            {label}
            <span
              aria-hidden="true"
              className={classNames(
                "absolute w-[7px] h-[7px] rotate-45 bg-gray-800",
                TIP_ARROW_CLASSES[tip],
              )}
            />
          </span>
        )}
      </span>
    );
  },
);

/**
 * A joined row of icon buttons — the toolbar / segmented treatment. Children
 * must be `IconButton` elements; borders collapse and only the outer corners
 * keep their radius.
 */
export function IconButtonGroup({ children }: { children: ReactNode }) {
  return (
    <span
      className={classNames(
        "inline-flex items-center",
        "[&>span>button]:rounded-none [&>span:not(:first-child)>button]:-ml-px",
        "[&>span:first-child>button]:rounded-l [&>span:last-child>button]:rounded-r",
        "[&>span:only-child>button]:rounded",
        "[&>span>button:hover]:z-[1] [&>span>button:focus-visible]:z-[1]",
      )}
    >
      {children}
    </span>
  );
}
